import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  Chip,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { styled } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDispatch, useSelector } from "react-redux";
import AttendanceService from "../../service/AttendanceService";
import {
  fetchEmployeeAttendance,
  fetchEmployeeAttendanceEmployees,
} from "../../action/EmployeeAttendanceAction";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import dayjs from "dayjs";

// ─── Color helpers ───────────────────────────────────────────────────────────
const rateColor = (pct) =>
  pct >= 80 ? "#16a34a" : pct >= 60 ? "#d97706" : "#dc2626";
const rateBg = (pct) =>
  pct >= 80 ? "#dcfce7" : pct >= 60 ? "#fef3c7" : "#fee2e2";
const rateTextColor = (pct) =>
  pct >= 80 ? "#15803d" : pct >= 60 ? "#92400e" : "#b91c1c";

// ─── Styled shells ────────────────────────────────────────────────────────────
const Page = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "0 0 80px",
}));

const Card = styled(Box)({
  background: "#fff",
  border: "0.5px solid #e2e8f0",
  borderRadius: 12,
  overflow: "hidden",
});

const SectionLabel = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: 6,
  marginTop: 14,
});

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, color }) {
  return (
    <Box
      sx={{
        flex: "1 1 0",
        background: "#fff",
        border: "0.5px solid #e2e8f0",
        borderRadius: "12px",
        p: "10px 12px",
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ─── Percentage badge ─────────────────────────────────────────────────────────
function PctBadge({ pct }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: "7px",
        py: "1px",
        borderRadius: "10px",
        fontSize: 11,
        fontWeight: 600,
        background: rateBg(pct),
        color: rateTextColor(pct),
      }}
    >
      {pct}%
    </Box>
  );
}

// ─── Custom chart tooltip ─────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Paper
      sx={{
        p: "10px 14px",
        border: "0.5px solid #e2e8f0",
        borderRadius: "8px",
        boxShadow: "0 4px 16px rgba(30,58,110,0.08)",
        minWidth: 150,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#330066", mb: 0.5 }}>
        {d.location}
      </Typography>
      {[
        { lbl: "Strength", val: d.total, color: "#1e293b" },
        { lbl: "Present", val: d.real, color: "#16a34a" },
        { lbl: "Rate", val: `${d.percentage}%`, color: rateColor(d.percentage) },
      ].map((r) => (
        <Box key={r.lbl} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Typography sx={{ fontSize: 11, color: "#64748b" }}>{r.lbl}</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: r.color }}>{r.val}</Typography>
        </Box>
      ))}
    </Paper>
  );
};

// ─── Swipe dot indicator ──────────────────────────────────────────────────────
function SwipeDots({ activeTab }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "5px",
        mb: "6px",
      }}
    >
      {[0, 1].map((i) => (
        <Box
          key={i}
          sx={{
            width: i === activeTab ? 16 : 6,
            height: 6,
            borderRadius: "10px",
            background: i === activeTab ? "#330066" : "#cbd5e1",
            transition: "all 0.3s ease",
          }}
        />
      ))}
      <Typography
        sx={{
          fontSize: 10,
          color: "#94a3b8",
          ml: "4px",
        }}
      >
        {activeTab === 0 ? "← swipe left for table" : "swipe right for chart →"}
      </Typography>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DETAIL VIEW
// ═══════════════════════════════════════════════════════════════════════════════
function LocationDetail({ location, date, division, employees, empLoading, onBack, formatTime }) {
  const isMobile = useMediaQuery("(max-width:600px)");

  const sorted = useMemo(() => {
    if (!employees?.length) return [];
    return [...employees].sort((a, b) => {
      // Out employees first
      const aHasOut = a.outTime ? 0 : 1;
      const bHasOut = b.outTime ? 0 : 1;
      if (aHasOut !== bHasOut) return aHasOut - bHasOut;
      // Then sort by inTime within each group
      if (!a.inTime) return 1;
      if (!b.inTime) return -1;
      return a.inTime.localeCompare(b.inTime);
    });
  }, [employees]);

  const present = sorted.filter((e) => e.inTime).length;
  const absent = sorted.length - present;
  const pct = sorted.length > 0 ? Math.round((present / sorted.length) * 100) : 0;

  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const at = new Date();
    const pw = doc.internal.pageSize.getWidth();
    doc.setFontSize(22).setFont("helvetica", "bold");
    doc.setTextColor(30, 58, 110);
    doc.text("DTS Biztrack", pw / 2, 20, { align: "center" });
    doc.setFontSize(13).setTextColor(100, 116, 139);
    doc.text(`Employee Attendance – ${location} (${division})`, pw / 2, 30, { align: "center" });
    doc.setDrawColor(30, 58, 110).setLineWidth(0.5).line(15, 36, pw - 15, 36);
    doc.setFontSize(10).setFont("helvetica", "normal").setTextColor(60, 60, 60);
    const y = 44, sx = 15, tw = pw - 30;
    const cols = [sx + tw * 0.15, sx + tw * 0.45, sx + tw * 0.8];
    [
      ["Date – ", date],
      ["Time – ", formatTime(at)],
      ["Total – ", sorted.length.toString()],
    ].forEach(([bold, normal], i) => {
      const fullW = doc.getTextWidth(bold + normal);
      doc.setFont("helvetica", "bold").text(bold, cols[i] - fullW / 2, y);
      doc.setFont("helvetica", "normal").text(normal, cols[i] - fullW / 2 + doc.getTextWidth(bold), y);
    });
    autoTable(doc, {
      startY: 52,
      head: [["Service No", "Name", "Designation", "In Time", "Status"]],
      body: sorted.map((e) => [
        e.serviceNo || "",
        e.name || "",
        e.designation || "",
        e.inTime || "NR",
        e.outTime ? `Out: ${e.outTime}` : e.inTime ? "Active" : "Absent",
      ]),
      styles: { fontSize: 10, cellPadding: 4 },
      headStyles: { fillColor: [30, 58, 110], textColor: 255, fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [248, 250, 255] },
      columnStyles: { 0: { cellWidth: 30, halign: "center" }, 3: { cellWidth: 30, halign: "center" }, 4: { cellWidth: 30, halign: "center" } },
      theme: "grid",
      didParseCell(data) {
        if (data.section !== "body") return;
        if (data.column.index === 3) {
          data.cell.styles.textColor = data.cell.raw === "NR" ? [220, 38, 38] : [22, 163, 74];
          data.cell.styles.fontStyle = "bold";
        }
        if (data.column.index === 4) {
          data.cell.styles.textColor = data.cell.raw === "Active" ? [22, 163, 74] : data.cell.raw === "Absent" ? [220, 38, 38] : [100, 116, 139];
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage(data) {
        doc.setFontSize(8).setTextColor(150).setFont("helvetica", "italic");
        doc.text(`Generated on ${date} at ${formatTime(at)}`, pw / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
        doc.text(`Page ${data.pageNumber}`, pw - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });
    doc.save(`Attendance_${location}_${date}.pdf`);
  };

  return (
    <Page>
      {/* ── Header row ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Box
          onClick={onBack}
          sx={{
            width: 32, height: 32, borderRadius: "8px",
            background: "#EBF0FA", display: "flex", alignItems: "center",
            justifyContent: "center", cursor: "pointer", flexShrink: 0,
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18, color: "#330066" }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#1e293b", lineHeight: 1.2 }}>
            {location}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#64748b" }}>{division}</Typography>
        </Box>
        <Button
          size="small"
          startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
          onClick={handleExportPDF}
          disabled={empLoading || !sorted.length}
          sx={{
            height: 32, borderRadius: "20px", background: "#16a34a",
            color: "#fff", fontSize: 12, fontWeight: 500, textTransform: "none",
            px: 1.5, flexShrink: 0, "&:hover": { background: "#15803d" },
            "&:disabled": { background: "#cbd5e1", color: "#64748b" },
          }}
        >
          PDF
        </Button>
      </Box>

      {/* ── Mini stats ── */}
      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
        <StatCard label="Strength" value={sorted.length} color="#330066" />
        <StatCard label="Present" value={present} color="#16a34a" />
        <StatCard label="Absent" value={absent} color="#dc2626" />
        <Box
          sx={{
            flex: "1 1 0", background: "#fff", border: "0.5px solid #e2e8f0",
            borderRadius: "12px", p: "10px 12px", minWidth: 0,
          }}
        >
          <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>Att %</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 500, color: rateColor(pct), lineHeight: 1.1 }}>
            {pct}%
          </Typography>
        </Box>
      </Box>

      {/* ── Date chip ── */}
      <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
        <CalendarTodayIcon sx={{ fontSize: 13, color: "#64748b" }} />
        <Typography sx={{ fontSize: 12, color: "#64748b" }}>{date}</Typography>
      </Box>

      {/* ── Employee table ── */}
      <Card>
        <Table size="small" sx={{ tableLayout: "auto", width: "100%" }}>
          <TableHead>
            <TableRow>
              {[
                { label: "Service No", w: "18%" },
                { label: "Name & Designation", w: "42%" },
                { label: "In Time", w: "18%" },
                { label: "Status", w: "22%" },
              ].map((h) => (
                <TableCell
                  key={h.label}
                  sx={{
                    width: h.w, background: "#330066", color: "#fff",
                    fontSize: isMobile ? 10 : 11, fontWeight: 600, py: "8px", px: "8px",
                    borderBottom: "none",
                  }}
                >
                  {h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {empLoading ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: "center", py: 5 }}>
                  <CircularProgress size={24} sx={{ color: "#330066" }} />
                </TableCell>
              </TableRow>
            ) : sorted.length > 0 ? (
              sorted.map((emp, i) => {
                const hasIn = !!emp.inTime;
                const statusLabel = hasIn ? (emp.outTime ? `Out ${emp.outTime}` : "Available") : "Absent";
                const statusColor = hasIn && !emp.outTime ? "#16a34a" : hasIn ? "#64748b" : "#dc2626";
                const statusBg = hasIn && !emp.outTime ? "#dcfce7" : hasIn ? "#f1f5f9" : "#fee2e2";
                return (
                  <TableRow
                    key={i}
                    sx={{
                      "&:nth-of-type(even) td": { background: "#F4F6FA" },
                      "&:last-child td": { borderBottom: "none" },
                    }}
                  >
                    <TableCell sx={{ fontSize: isMobile ? 11 : 12, fontWeight: 600, px: "8px", py: "7px" }}>
                      {emp.serviceNo}
                    </TableCell>
                    <TableCell sx={{ px: "8px", py: "7px" }}>
                      <Typography sx={{ fontSize: isMobile ? 11 : 12, fontWeight: 600, color: "#1A2B45", lineHeight: 1.3 }}>
                        {emp.name}
                      </Typography>
                      {emp.designation && (
                        <Typography sx={{ fontSize: 10, color: "#6B7A99", lineHeight: 1.2, mt: "2px" }}>
                          {emp.designation}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ px: "8px", py: "7px" }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block", px: "6px", py: "1px",
                          borderRadius: "8px", fontSize: 10, fontWeight: 600,
                          background: hasIn ? "#dcfce7" : "#fee2e2",
                          color: hasIn ? "#15803d" : "#b91c1c",
                        }}
                      >
                        {hasIn ? emp.inTime : "NR"}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: "8px", py: "7px" }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block", px: "6px", py: "1px",
                          borderRadius: "8px", fontSize: 10, fontWeight: 600,
                          background: statusBg, color: statusColor,
                        }}
                      >
                        {statusLabel}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: "center", py: 5, color: "#94a3b8", fontSize: 13 }}>
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </Page>
  );
}


// MAIN COMPONENT
export default function EmployeeAttendance() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { loading, data, empLoading, empData } = useSelector((s) => s.employeeAttendance);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [showDetail, setShowDetail] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState("DPM");

  // ── Swipe state ──
  const [activeTab, setActiveTab] = useState(0); // 0 = chart, 1 = table
  const swipeStartX = useRef(null);
  const swipeStartY = useRef(null);
  const isHorizontalSwipe = useRef(false);
  const mouseStartX = useRef(null);

  const chartContainerRef = useRef(null);

  const formatDate = (d) => dayjs(d).format("YYYY-MM-DD");
  const formatTime = (d) => new Date(d).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  const displayDivisions = useMemo(() => {
    const list = [...divisions];
    if (!list.some((d) => d.DivisionCode === "DPM"))
      list.push({ DivisionCode: "DPM", DivisionName: "Project and People Management" });
    return list;
  }, [divisions]);

  useEffect(() => {
    AttendanceService.GetAllDivisions()
      .then((res) => setDivisions(res?.data?.ResultSet || res?.data || []))
      .catch(console.error);
  }, []);

  useEffect(() => {
    dispatch(fetchEmployeeAttendance(formatDate(selectedDate), selectedDivision));
  }, [dispatch, selectedDivision]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    dispatch(fetchEmployeeAttendance(formatDate(date), selectedDivision));
  };

  const handleDivisionChange = (e) => {
    const div = e.target.value;
    setSelectedDivision(div);
    dispatch(fetchEmployeeAttendance(formatDate(selectedDate), div));
  };

  const handleLocationClick = (location) => {
    setSelectedLocation(location);
    setTimeout(() => {
      setShowDetail(true);
      dispatch(fetchEmployeeAttendanceEmployees(formatDate(selectedDate), location, selectedDivision));
    }, 150);
  };

  // ── Touch swipe handlers ──
  const handleTouchStart = (e) => {
    swipeStartX.current = e.touches[0].clientX;
    swipeStartY.current = e.touches[0].clientY;
    isHorizontalSwipe.current = false;
  };

  const handleTouchMove = (e) => {
    if (swipeStartX.current === null) return;
    const dx = Math.abs(e.touches[0].clientX - swipeStartX.current);
    const dy = Math.abs(e.touches[0].clientY - swipeStartY.current);
    if (dx > dy && dx > 8) {
      isHorizontalSwipe.current = true;
    }
  };

  const handleTouchEnd = (e) => {
    if (!isHorizontalSwipe.current || swipeStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - swipeStartX.current;
    if (dx < -40 && activeTab === 0) setActiveTab(1);
    else if (dx > 40 && activeTab === 1) setActiveTab(0);
    swipeStartX.current = null;
    swipeStartY.current = null;
    isHorizontalSwipe.current = false;
  };

  // ── Mouse drag handlers (desktop) ──
  const handleMouseDown = (e) => {
    mouseStartX.current = e.clientX;
  };

  const handleMouseUp = (e) => {
    if (mouseStartX.current === null) return;
    const dx = e.clientX - mouseStartX.current;
    if (dx < -50 && activeTab === 0) setActiveTab(1);
    else if (dx > 50 && activeTab === 1) setActiveTab(0);
    mouseStartX.current = null;
  };

  // Derived data
  const sortedData = useMemo(
    () => (data ? [...data].sort((a, b) => a.Emp_location.localeCompare(b.Emp_location)) : []),
    [data]
  );

  const chartData = useMemo(
    () =>
      sortedData
        .filter((item) => Number(item.Emp_real || 0) > 0)
        .map((item) => ({
          location: item.Emp_location,
          total: Number(item.Emp_Total),
          real: Number(item.Emp_real),
          percentage: Math.round(Number(item.Percentage)),
        })),
    [sortedData]
  );

  const totalStrength = data?.length ? data.reduce((s, i) => s + Number(i.Emp_Total || 0), 0) : 0;
  const activeCount = data?.length ? data.reduce((s, i) => s + Number(i.Emp_real || 0), 0) : 0;
  const absentCount = totalStrength - activeCount;
  const activePercentage = totalStrength > 0 ? Math.round((activeCount / totalStrength) * 100) : 0;

  const totalOut = !loading && data?.length > 0 ? data.reduce((sum, item) => sum + Number(item.Emp_Out || 0), 0) : 0;
  const totalAvailable = !loading && data?.length > 0 ? data.reduce((sum, item) => sum + Number(item.Emp_Available || 0), 0) : 0;
  const totalOutPercentage = activeCount > 0 ? Math.round((totalOut / activeCount) * 100) : 0;
  const totalAvailablePercentage = activeCount > 0 ? Math.round((totalAvailable / activeCount) * 100) : 0;

  // PDF exports
  const handleExportTablePDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const at = new Date();
    const pw = doc.internal.pageSize.getWidth();

    doc.setFontSize(22).setFont("helvetica", "bold").setTextColor(30, 58, 110);
    doc.text("DTS Biztrack", pw / 2, 20, { align: "center" });
    doc.setFontSize(13).setTextColor(100, 116, 139);
    doc.text(`Employee Strength & Attendance – ${selectedDivision}`, pw / 2, 30, { align: "center" });
    doc.setDrawColor(30, 58, 110).setLineWidth(0.5).line(15, 36, pw - 15, 36);

    const pairs = [
      ["Date – ", formatDate(selectedDate)],
      ["Time – ", formatTime(at)],
      ["Strength –   ", totalStrength.toString()],
      ["Attendance –   ", `${activeCount} (${activePercentage}%)`],
    ];
    const sx = 15, tw = pw - 30;
    pairs.forEach(([bold, val], i) => {
      const col = sx + tw * (0.125 + i * 0.25);
      const fw = doc.getTextWidth(bold + val);
      doc.setFontSize(10).setFont("helvetica", "bold").setTextColor(60, 60, 60);
      doc.text(bold, col - fw / 2, 44);
      doc.setFont("helvetica", "normal").text(val, col - fw / 2 + doc.getTextWidth(bold), 44);
    });

    const columns = ["Location", "Strength", "Attendance", "Att %", "Out", "Available"];

    const rows = sortedData.map((item) => [
      item.Emp_location || "",
      item.Emp_Total || "",
      item.Emp_real || "",
      `${Math.round(Number(item.Percentage))}%`,
      item.Emp_Out || "0",
      item.Emp_Available || "0",
    ]);

    rows.push([
      "Total",
      totalStrength.toString(),
      activeCount.toString(),
      `${activePercentage}%`,
      totalOut.toString(),
      totalAvailable.toString(),
    ]);

    autoTable(doc, {
      startY: 52,
      head: [columns],
      body: rows,
      styles: {
        fontSize: 10,
        cellPadding: 5,
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [30, 58, 110],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 10,
        halign: "center",
        cellPadding: 6,
        valign: "middle",
      },
      alternateRowStyles: { fillColor: [248, 250, 255] },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 28 },
        2: { cellWidth: 28 },
        3: { cellWidth: 28 },
        4: { cellWidth: 34 },
        5: { cellWidth: 34 },
      },
      margin: { left: 14, right: 14 },
      theme: "grid",
      didParseCell(data) {
        const isTotalRow = data.row.index === rows.length - 1;
        if (isTotalRow) {
          data.cell.styles.fillColor = [30, 58, 110];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
          if (data.column.index === 4 || data.column.index === 5) {
            data.cell.styles.minCellHeight = 18;
          }
        }
      },
      didDrawCell(data) {
        const isTotalRow = data.row.index === rows.length - 1;

        // Out column — percentage below count on total row only
        if (isTotalRow && data.section === "body" && data.column.index === 4) {
          doc.setFontSize(8).setFont("helvetica", "normal");
          doc.setTextColor(255, 205, 210);
          const x = data.cell.x + data.cell.width / 2;
          const y = data.cell.y + data.cell.height - 4;
          doc.text(`(${totalOutPercentage}%)`, x, y, { align: "center" });
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
        }

        // Available column — percentage below count on total row only
        if (isTotalRow && data.section === "body" && data.column.index === 5) {
          doc.setFontSize(8).setFont("helvetica", "normal");
          doc.setTextColor(185, 246, 202);
          const x = data.cell.x + data.cell.width / 2;
          const y = data.cell.y + data.cell.height - 4;
          doc.text(`(${totalAvailablePercentage}%)`, x, y, { align: "center" });
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
        }
      },
      didDrawPage(data) {
        doc.setFontSize(8).setTextColor(150).setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(at)} at ${formatTime(at)}`,
          pw / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pw - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`Attendance_Table_${formatDate(selectedDate)}.pdf`);
  };

  const handleExportChartPDF = async () => {
    if (!chartContainerRef.current) return;
    try {
      const canvas = await html2canvas(chartContainerRef.current, { scale: 2, backgroundColor: "#fff", useCORS: true });
      const doc = new jsPDF("p", "mm", "a4");
      const at = new Date();
      const pw = doc.internal.pageSize.getWidth(), ph = doc.internal.pageSize.getHeight();
      doc.setFontSize(22).setFont("helvetica", "bold").setTextColor(30, 58, 110);
      doc.text("DTS Biztrack", pw / 2, 20, { align: "center" });
      doc.setFontSize(13).setTextColor(100, 116, 139);
      doc.text(`Attendance Chart – ${selectedDivision}`, pw / 2, 30, { align: "center" });
      doc.setDrawColor(30, 58, 110).setLineWidth(0.5).line(15, 36, pw - 15, 36);
      const cw = pw - 20, ch = Math.min(cw / (canvas.width / canvas.height), ph - 52 - 15);
      doc.addImage(canvas.toDataURL("image/png"), "PNG", (pw - cw) / 2, 42, cw, ch);
      doc.setFontSize(8).setTextColor(150).setFont("helvetica", "italic");
      doc.text(`Generated on ${formatDate(at)} at ${formatTime(at)}`, pw / 2, ph - 10, { align: "center" });
      doc.save(`Attendance_Chart_${formatDate(selectedDate)}.pdf`);
    } catch (err) {
      console.error(err);
    }
  };

  // ── Detail view
  if (showDetail) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <LocationDetail
          location={selectedLocation}
          date={formatDate(selectedDate)}
          division={selectedDivision}
          employees={empData || []}
          empLoading={empLoading}
          onBack={() => { setShowDetail(false); setSelectedLocation(""); }}
          formatTime={formatTime}
        />
      </LocalizationProvider>
    );
  }

  // ── Summary view
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Page>
        {/* ── Filter row ── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
          {/* Division select */}
          <FormControl sx={{ flex: 1, minWidth: 0 }} size="small">
            <Select
              value={selectedDivision}
              onChange={handleDivisionChange}
              sx={{
                height: 36, borderRadius: "20px", background: "#fff",
                fontSize: 12, fontWeight: 500, color: "#1e293b",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#330066" },
                "& .MuiSelect-select": { py: 0 },
              }}
            >
              {displayDivisions.map((d) => (
                <MenuItem key={d.DivisionCode} value={d.DivisionCode} sx={{ fontSize: 13 }}>
                  {d.DivisionCode} – {d.DivisionName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date picker trigger */}
          <Box
            onClick={() => setOpenDatePicker(true)}
            sx={{
              height: 36, borderRadius: "20px", border: "0.5px solid #e2e8f0",
              background: "#fff", display: "flex", alignItems: "center",
              gap: "6px", px: "12px", cursor: "pointer", flexShrink: 0,
              "&:hover": { borderColor: "#330066" },
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography sx={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>
              {formatDate(selectedDate)}
            </Typography>
          </Box>

          {openDatePicker && (
            <DatePicker
              open={openDatePicker}
              value={selectedDate}
              onChange={handleDateChange}
              onClose={() => setOpenDatePicker(false)}
              renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
            />
          )}
        </Box>

        {/* ── Stats ── */}
        {!loading && data?.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <StatCard label="Strength" value={totalStrength} color="#330066" />
            <StatCard label="Present" value={activeCount} color="#16a34a" />
            <StatCard label="Absent" value={absentCount} color="#dc2626" />
            <Box sx={{ flex: "1 1 0", background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: "12px", p: "10px 12px", minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>Att. %</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 500, color: rateColor(activePercentage), lineHeight: 1.1 }}>
                {activePercentage}%
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── PDF buttons ── */}
        {!loading && data?.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <Button
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
              onClick={handleExportChartPDF}
              disabled={!data?.length}
              sx={{
                flex: 1, height: 34, borderRadius: "20px",
                background: "#330066", color: "#fff", fontSize: 12,
                fontWeight: 500, textTransform: "none",
                "&:hover": { background: "#330066" },
                "&:disabled": { background: "#cbd5e1", color: "#64748b" },
              }}
            >
              Chart PDF
            </Button>
            <Button
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
              onClick={handleExportTablePDF}
              disabled={!data?.length}
              sx={{
                flex: 1, height: 34, borderRadius: "20px",
                background: "#16a34a", color: "#fff", fontSize: 12,
                fontWeight: 500, textTransform: "none",
                "&:hover": { background: "#15803d" },
                "&:disabled": { background: "#cbd5e1", color: "#64748b" },
              }}
            >
              Table PDF
            </Button>
          </Box>
        )}

        {/* ── Swipeable Chart / Table ── */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: "#330066" }} />
          </Box>
        ) : chartData.length > 0 ? (
          <>
            {/* Dot indicator + hint */}
            <SwipeDots activeTab={activeTab} />

            <Box
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              sx={{
                position: "relative",
                width: "100%",
                userSelect: "none",
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
              }}
            >
              {/* Clip wrapper */}
              <Box sx={{ overflow: "hidden", borderRadius: "12px", border: "0.5px solid #e2e8f0" }}>
                {/* Sliding track */}
                <Box
                  sx={{
                    display: "flex",
                    width: "200%",
                    transform: `translateX(-${activeTab * 50}%)`,
                    transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                    willChange: "transform",
                  }}
                >
                  {/* ── SLIDE 0: Chart (exact original markup) ── */}
                  <Box sx={{ width: "50%", flexShrink: 0 }}>
                    <Box ref={chartContainerRef} sx={{ background: "#fff", p: "12px 12px 8px" }}>
                      {/* Section label */}
                      <Typography
                        sx={{
                          fontSize: 11, fontWeight: 600, color: "#94a3b8",
                          textTransform: "uppercase", letterSpacing: "0.6px", mb: "10px",
                        }}
                      >
                        Attendance by Location ({chartData.length} locations)
                      </Typography>

                      {/* Location bars */}
                      {chartData.map((row) => {
                        const barW = Math.round((row.real / Math.max(...chartData.map((r) => r.total))) * 100);
                        return (
                          <Box
                            key={row.location}
                            onClick={() => handleLocationClick(row.location)}
                            sx={{ mb: "10px", cursor: "pointer", "&:last-child": { mb: 0 } }}
                          >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "3px" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <LocationOnIcon sx={{ fontSize: 12, color: "#330066" }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                                  {row.location}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                  {row.real}/{row.total}
                                </Typography>
                                <PctBadge pct={row.percentage} />
                              </Box>
                            </Box>
                            <Box sx={{ height: 5, background: "#f1f5f9", borderRadius: "10px", overflow: "hidden" }}>
                              <Box
                                sx={{
                                  height: "100%", borderRadius: "10px",
                                  width: `${barW}%`,
                                  background: rateColor(row.percentage),
                                  transition: "width 0.5s ease",
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })}

                      {/* Legend */}
                      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", pt: "10px", mt: "8px", borderTop: "0.5px solid #f1f5f9" }}>
                        {[{ label: "≥80%", color: "#16a34a" }, { label: "60–79%", color: "#92400e" }, { label: "<60%", color: "#b91c1c" }].map((l) => (
                          <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: l.color }} />
                            <Typography sx={{ fontSize: 10, color: "#64748b" }}>{l.label}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  {/* Location Summary Table */}
                  <Box sx={{ width: "50%", flexShrink: 0 }}>
                    <Card sx={{ borderRadius: 0, border: "none" }}>
                      {/* Section label inside slide */}
                      <Box sx={{ px: "10px", pt: "10px", pb: "6px" }}>
                        <Typography
                          sx={{
                            fontSize: 11, fontWeight: 600, color: "#94a3b8",
                            textTransform: "uppercase", letterSpacing: "0.6px",
                          }}
                        >
                          Location Summary
                        </Typography>
                      </Box>
                      <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                        <TableHead>
                          <TableRow>
                            {[
                              { label: "Location", w: "38%" },
                              { label: "Total", w: "13%" },
                              { label: "Att", w: "13%" },
                              { label: "%", w: "13%" },
                              { label: "Out", w: "13%" },
                              { label: "Bal", w: "13%" },
                            ].map((h) => (
                              <TableCell
                                key={h.label}
                                sx={{
                                  width: h.w, background: "#330066", color: "#fff",
                                  fontSize: isMobile ? 10 : 11, fontWeight: 600,
                                  py: "8px", px: "10px", borderBottom: "none",
                                  textAlign: h.label !== "Location" ? "center" : "left",
                                }}
                              >
                                {h.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedData.map((row, i) => {
                            const rate = Math.round(Number(row.Percentage));
                            return (
                              <TableRow
                                key={i}
                                onClick={() => handleLocationClick(row.Emp_location)}
                                sx={{
                                  cursor: "pointer",
                                  "&:nth-of-type(even) td": { background: "#F4F6FA" },
                                  "&:hover td": { background: "rgba(30,58,110,0.04)" },
                                  "&:last-child td": { borderBottom: "none" },
                                }}
                              >
                                <TableCell sx={{ px: "10px", py: "8px" }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                    <LocationOnIcon sx={{ fontSize: 12, color: "#330066" }} />
                                    <Typography sx={{ fontSize: isMobile ? 12 : 13, fontWeight: 600 }}>
                                      {row.Emp_location}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                                  <Box component="span" sx={{ display: "inline-block", px: "8px", py: "2px", borderRadius: "10px", fontSize: 11, fontWeight: 600, background: "#dce8f7", color: "#330066" }}>
                                    {row.Emp_Total}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                                  <Box component="span" sx={{ display: "inline-block", px: "8px", py: "2px", borderRadius: "10px", fontSize: 11, fontWeight: 600, background: "#dcfce7", color: "#16a34a" }}>
                                    {row.Emp_real}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                                  <PctBadge pct={rate} />
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                                  <Box component="span" sx={{ display: "inline-block", px: "8px", py: "2px", borderRadius: "10px", fontSize: 11, fontWeight: 600, background: "#fef2f2", color: "#b91c1c" }}>
                                    {row.Emp_Out}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                                  <Box component="span" sx={{ display: "inline-block", px: "8px", py: "2px", borderRadius: "10px", fontSize: 11, fontWeight: 600, background: "#dcfce7", color: "#16a34a" }}>
                                    {row.Emp_Available}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}

{/* Total row */}
<TableRow sx={{ "& td": { background: "#EFF4FF !important", borderBottom: "none", verticalAlign: "top", paddingTop: "10px" } }}>
  <TableCell sx={{ px: "10px", py: "8px", paddingTop: "10px", fontSize: 12, fontWeight: 600, color: "#330066", verticalAlign: "top" }}>
    Total
  </TableCell>
  <TableCell sx={{ textAlign: "center", px: "6px", paddingTop: "10px", verticalAlign: "top" }}>
    <Box component="span" sx={{ display: "inline-block", px: "8px", py: "2px", borderRadius: "10px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#330066", border: "0.5px solid #c7d7f8" }}>
      {totalStrength}
    </Box>
  </TableCell>
  <TableCell sx={{ textAlign: "center", px: "6px", paddingTop: "10px", verticalAlign: "top" }}>
    <Box component="span" sx={{ display: "inline-block", px: "8px", py: "2px", borderRadius: "10px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#16a34a", border: "0.5px solid #bbf7d0" }}>
      {activeCount}
    </Box>
  </TableCell>
  <TableCell sx={{ textAlign: "center", px: "6px", paddingTop: "10px", verticalAlign: "top" }}>
    <PctBadge pct={activePercentage} />
  </TableCell>
  <TableCell sx={{ textAlign: "center", px: "6px", paddingTop: "10px", verticalAlign: "top" }}>
    <Box component="span" sx={{ display: "inline-block", px: "8px", py: "2px", borderRadius: "10px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#dc2626", border: "0.5px solid #fecaca" }}>
      {totalOut}
    </Box>
    <Typography sx={{ fontSize: 9, color: "#dc2626", fontWeight: 500, mt: "2px" }}>
      ({totalOutPercentage}%)
    </Typography>
  </TableCell>
  <TableCell sx={{ textAlign: "center", px: "6px", paddingTop: "10px", verticalAlign: "top" }}>
    <Box component="span" sx={{ display: "inline-block", px: "8px", py: "2px", borderRadius: "10px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#16a34a", border: "0.5px solid #bbf7d0" }}>
      {totalAvailable}
    </Box>
    <Typography sx={{ fontSize: 9, color: "#16a34a", fontWeight: 500, mt: "2px" }}>
      ({totalAvailablePercentage}%)
    </Typography>
  </TableCell>
</TableRow>
                        </TableBody>
                      </Table>
                    </Card>
                  </Box>

                </Box>
              </Box>
            </Box>
          </>
        ) : (
          !loading && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography sx={{ fontSize: 14, color: "#94a3b8" }}>No attendance data found</Typography>
            </Box>
          )
        )}
      </Page>
    </LocalizationProvider>
  );
}